import React, { useState, Suspense, useMemo } from "react"
import { Canvas, useThree } from "@react-three/fiber"
import { EffectComposer, Bloom } from "@react-three/postprocessing"
import ParticleField from "./ParticleField"
import { useCursorPosition } from "./useCursorPosition"
import { resolveTheme, ParticleTheme } from "./particleThemes"
import { registerParticleCanvas } from "../cosmos/particleCapture"
import { useCosmos } from "../cosmos/cosmosStore"

interface SceneContentProps {
  theme: ParticleTheme
}

const SceneContent: React.FC<SceneContentProps> = ({ theme }) => {
  const cursorRef = useCursorPosition()
  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 550

  return (
    <>
      <ParticleField
        cursorRef={cursorRef}
        count={isMobile ? 350 : 900}
        theme={theme}
      />
      {!isMobile && (
        <EffectComposer>
          <Bloom
            luminanceThreshold={0.2}
            luminanceSmoothing={0.9}
            intensity={0.8}
          />
        </EffectComposer>
      )}
    </>
  )
}

/**
 * Lets the wormhole journey capture this canvas in the frame it renders, and
 * stops drawing particles while the journey covers the page.
 */
const ParticleCaptureBridge: React.FC = () => {
  const gl = useThree((s) => s.gl)
  const setFrameloop = useThree((s) => s.setFrameloop)
  const journey = useCosmos((s) => s.mode === "journey")
  React.useEffect(() => registerParticleCanvas(() => gl.domElement), [gl])
  React.useEffect(() => {
    setFrameloop(journey ? "never" : "always")
  }, [journey, setFrameloop])
  return null
}

function hasWebGL(): boolean {
  if (typeof window === "undefined") return false
  try {
    const canvas = document.createElement("canvas")
    return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"))
  } catch {
    return false
  }
}

interface ParticleBackgroundProps {
  pagePath?: string
}

const ParticleBackground: React.FC<ParticleBackgroundProps> = ({
  pagePath = "/",
}) => {
  const [isClient, setIsClient] = useState(false)
  const [webglAvailable, setWebglAvailable] = useState(true)

  const theme = useMemo(() => resolveTheme(pagePath), [pagePath])

  React.useEffect(() => {
    setIsClient(true)
    setWebglAvailable(hasWebGL())
  }, [])

  if (!isClient || !webglAvailable) return null

  return (
    <Canvas
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
      camera={{ position: [0, 0, 8], fov: 60 }}
      gl={{ antialias: true, alpha: true }}
      dpr={[1, 2]}
    >
      <ParticleCaptureBridge />
      <Suspense fallback={null}>
        <SceneContent theme={theme} />
      </Suspense>
    </Canvas>
  )
}

export default ParticleBackground
